import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Zap, Car, BatteryCharging, Wrench, BookOpen, Settings, ChevronRight, PenTool } from 'lucide-react';

const ElectricVehicle: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Institute of E-Mobility";
    
    return (
        <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-teal-100 selection:text-teal-900">
            {/* Navbar */}
            <nav className="fixed w-full z-50 px-8 py-5 bg-white/95 backdrop-blur border-b border-slate-100">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center text-white shadow-sm">
                            <Zap size={20} fill="white" />
                        </div>
                        <span className="font-bold text-xl tracking-tight text-slate-800">{schoolName}</span>
                    </div>
                    
                    <div className="hidden md:flex gap-10 text-sm font-medium text-slate-500">
                        <a href="#" className="hover:text-teal-600 transition-colors">Programs</a>
                        <a href="#" className="hover:text-teal-600 transition-colors">Research</a>
                        <a href="#" className="hover:text-teal-600 transition-colors">Industry Partners</a>
                        <a href="#" className="hover:text-teal-600 transition-colors">Admissions</a>
                    </div>

                    <div className="flex gap-4">
                        <Button variant="ghost" className="text-slate-600 font-bold hover:bg-slate-50">
                            Login
                        </Button>
                        <Button className="rounded-md bg-teal-600 hover:bg-teal-700 text-white px-6 font-bold shadow-md transition-all">
                            Apply Now
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative pt-32 pb-24 px-6 overflow-hidden bg-slate-50">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div className="order-2 lg:order-1 relative z-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-bold uppercase tracking-wide mb-6">
                           <BatteryCharging size={14} /> Training the Workforce of Tomorrow
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold text-slate-900 leading-tight mb-6 tracking-tight">
                            Engineer the <br/>
                            <span className="text-teal-600">Electric Future.</span>
                        </h1>
                        <p className="text-lg text-slate-600 max-w-lg mb-8 leading-relaxed">
                            Join the leading institution for automotive engineering, battery technology, and sustainable transportation systems.
                        </p>
                        
                        <div className="flex gap-4">
                            <Button className="h-14 px-8 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md">
                                View Degree Programs
                            </Button>
                            <Button variant="outline" className="h-14 px-8 border-slate-300 text-slate-700 hover:bg-white font-bold rounded-md">
                                Campus Tour
                            </Button>
                        </div>
                    </div>

                    {/* Hero Image */}
                    <div className="order-1 lg:order-2 relative aspect-[4/3] bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200 group">
                        <div className="absolute inset-0 bg-gradient-to-tr from-teal-500/20 to-blue-500/10 mix-blend-multiply"></div>
                        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1593941707882-a5bba14938c7?q=80&w=2072&auto=format&fit=crop')] bg-cover bg-center transition-transform duration-700 group-hover:scale-105"></div>
                        
                        <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur p-6 rounded-xl shadow-lg border border-slate-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-1">Student Project</div>
                                    <h3 className="text-lg font-bold text-slate-900">Prototype Chassis Design</h3>
                                </div>
                                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-400">
                                    <Settings size={20} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Stats */}
            <section className="py-12 bg-white border-y border-slate-100">
                <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center bg-white">
                    <div>
                        <div className="text-4xl font-bold text-teal-600 mb-2">100%</div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Job Placement</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-teal-600 mb-2">5</div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Advanced Labs</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-teal-600 mb-2">$20M</div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Research Grants</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-teal-600 mb-2">Global</div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Partnerships</div>
                    </div>
                </div>
            </section>

            {/* Curriculum */}
            <section className="py-24 px-6 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">Specialized Tracks</h2>
                        <p className="text-slate-500 max-w-2xl mx-auto">
                             Our curriculum is developed in partnership with major automotive manufacturers like Tesla, Rivian, and Ford.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: Wrench, title: "Maintenance & Diagnostics", desc: "Hands-on training with high-voltage systems and computerized diagnostics." },
                            { icon: BatteryCharging, title: "Battery Technology", desc: "Chemical engineering and thermal management systems for EV batteries." },
                            { icon: PenTool, title: "Design & manufacturing", desc: "CAD modeling, prototyping, and assembly line automation." }
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:shadow-lg hover:border-teal-200 transition-all hover:-translate-y-1 group cursor-pointer">
                                <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                                    <item.icon size={26} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-500 leading-relaxed mb-6">{item.desc}</p>
                                <div className="flex items-center text-teal-600 font-bold text-sm">
                                    <span className="mr-2">Syllabus</span>
                                    <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-white text-slate-600 py-16 px-6 border-t border-slate-200">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                     <div className="col-span-1 md:col-span-1">
                        <div className="flex items-center gap-2 mb-6 text-slate-900">
                            <div className="w-8 h-8 bg-teal-600 rounded flex items-center justify-center text-white">
                                <Zap size={16} fill="white" />
                            </div>
                            <span className="font-bold text-lg">{schoolName}</span>
                        </div>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            Certified automotive training for the 21st century.
                        </p>
                    </div>
                     <div>
                        <h4 className="font-bold text-slate-900 mb-4 text-sm uppercase tracking-wider">Academics</h4>
                        <ul className="space-y-3 text-sm">
                            <li><a href="#" className="hover:text-teal-600">Degrees</a></li>
                            <li><a href="#" className="hover:text-teal-600">Certificates</a></li>
                            <li><a href="#" className="hover:text-teal-600">Apprenticeships</a></li>
                        </ul>
                    </div>
                     <div>
                        <h4 className="font-bold text-slate-900 mb-4 text-sm uppercase tracking-wider">Admissions</h4>
                        <ul className="space-y-3 text-sm">
                            <li><a href="#" className="hover:text-teal-600">Apply</a></li>
                            <li><a href="#" className="hover:text-teal-600">Tuition</a></li>
                            <li><a href="#" className="hover:text-teal-600">Open House</a></li>
                        </ul>
                    </div>
                     <div>
                        <h4 className="font-bold text-slate-900 mb-4 text-sm uppercase tracking-wider">Contact</h4>
                        <ul className="space-y-3 text-sm">
                            <li>info@emobility.edu</li>
                            <li>1-800-EV-TECH</li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-100 text-center text-xs text-slate-400">
                    &copy; 2026 {schoolName}. All rights reserved under the Department of Education.
                </div>
            </footer>
        </div>
    );
};

export default ElectricVehicle;
